import type {
  ReportGridColumnSettings,
  ReportGridSettingsEntry,
} from 'ag-common-lib/lib/models/reporting/report-grid-settings.model';

/**
 * What a viewer changed about a grid, worked out by comparing it against the grid itself.
 *
 * Nothing here stores a whole DevExtreme state. A stored state pins every column property
 * whether or not anyone set it, so it masks any default changed in code afterwards, and the
 * only way to unmask one is to discard every saved layout. Storing the difference instead
 * means an untouched property is simply absent and keeps taking whatever the grid declares
 * today — so defaults can change without anyone losing their arrangement.
 *
 * The default is never stored either: it is read back from the grid on each load, before the
 * viewer's settings go on top, so it cannot go stale.
 */

/** The column properties a viewer can change. Anything outside this list is not ours to keep. */
const TRACKED_COLUMN_PROPERTIES = [
  'visible',
  'width',
  'visibleIndex',
  'sortOrder',
  'sortIndex',
  'filterValue',
  'filterValues',
  // Without it a header filter the viewer built by clearing names from "Select all" came back
  // keeping only those names — the exact opposite of what they had chosen.
  'filterType',
  'selectedFilterOperation',
] as const;

type TrackedColumnProperty = (typeof TRACKED_COLUMN_PROPERTIES)[number];

export type ComparisonGridStateColumn = {
  dataField?: string;
  name?: string;
} & Partial<Record<TrackedColumnProperty, unknown>>;

export type ComparisonGridState = {
  columns?: ComparisonGridStateColumn[];
  pageSize?: unknown;
  [key: string]: unknown;
};

const columnKey = (column: ComparisonGridStateColumn): string => String(column.dataField ?? column.name ?? '');

/** undefined and null both mean "not set", and DevExtreme uses them interchangeably. */
const isSameValue = (left: unknown, right: unknown): boolean =>
  JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

const cloneState = (state: ComparisonGridState): ComparisonGridState =>
  JSON.parse(JSON.stringify(state ?? {})) as ComparisonGridState;

/**
 * The default a grid is judged against, once the page-wide column choice is on it.
 *
 * Without this the page-wide choice would land in every grid's difference, so hiding one
 * column would mark all twelve grids as changed — and showing it again later would leave
 * twelve stored overrides still hiding it.
 */
export function buildComparisonEffectiveDefault(
  pristine: ComparisonGridState,
  hiddenColumns: ReadonlySet<string>,
): ComparisonGridState {
  const effective = cloneState(pristine);
  for (const column of effective.columns ?? []) {
    const key = columnKey(column);
    if (key && hiddenColumns.has(key)) {
      column.visible = false;
    }
  }

  return effective;
}

/** What the viewer changed, and only that. Absent when they have changed nothing. */
export function buildComparisonGridDiff(
  current: ComparisonGridState,
  effectiveDefault: ComparisonGridState,
): ReportGridSettingsEntry {
  const defaultsByKey = new Map<string, ComparisonGridStateColumn>();
  for (const column of effectiveDefault?.columns ?? []) {
    const key = columnKey(column);
    if (key) {
      defaultsByKey.set(key, column);
    }
  }

  const columns: Record<string, ReportGridColumnSettings> = {};
  for (const column of current?.columns ?? []) {
    const key = columnKey(column);
    const shipped = key ? defaultsByKey.get(key) : undefined;
    if (!key || !shipped) {
      continue;
    }

    const changed: Record<string, unknown> = {};
    for (const property of TRACKED_COLUMN_PROPERTIES) {
      if (!isSameValue(column[property], shipped[property])) {
        changed[property] = column[property] ?? null;
      }
    }

    if (Object.keys(changed).length) {
      columns[key] = changed as ReportGridColumnSettings;
    }
  }

  const entry: ReportGridSettingsEntry = {};
  if (Object.keys(columns).length) {
    entry.columns = columns;
  }

  if (!isSameValue(current?.pageSize, effectiveDefault?.pageSize) && typeof current?.pageSize === 'number') {
    entry.pageSize = current.pageSize;
  }

  return entry;
}

/** The state to hand a grid: what it ships with, with the viewer's changes laid over it. */
export function applyComparisonGridDiff(
  effectiveDefault: ComparisonGridState,
  entry: ReportGridSettingsEntry | undefined,
): ComparisonGridState {
  const state = cloneState(effectiveDefault);
  if (!entry) {
    return state;
  }

  for (const column of state.columns ?? []) {
    const changed = entry.columns?.[columnKey(column)];
    if (!changed) {
      continue;
    }

    for (const [property, value] of Object.entries(changed)) {
      // A column the viewer has not sorted stores null, which DevExtreme reads as "no sort".
      (column as Record<string, unknown>)[property] = value;
    }
  }

  if (typeof entry.pageSize === 'number') {
    state.pageSize = entry.pageSize;
  }

  return state;
}

/**
 * Whether the grid's own layout is still as it ships, which is what decides if Reset is worth
 * offering.
 *
 * A mover table's own settings are deliberately not counted. Every mover writes those on its
 * first load, so counting them would offer Reset on every table for ever; whether they differ
 * from the defaults is the table state's own question, and it answers it.
 */
export function isComparisonGridLayoutEmpty(entry: ReportGridSettingsEntry | undefined): boolean {
  return !entry?.columns && entry?.pageSize === undefined;
}

/** Whether there is anything at all worth storing for this grid. */
export function isComparisonGridEntryEmpty(entry: ReportGridSettingsEntry | undefined): boolean {
  return isComparisonGridLayoutEmpty(entry) && !entry?.table;
}
